import {
	RiAddBoxLine,
	RiApps2Line,
	RiBankCardLine,
	RiCodeSSlashLine,
	RiEarthLine,
	RiGiftLine,
	RiTabletLine,
	RiTruckLine,
	RiWindowLine,
} from 'react-icons/ri';
import { PlanDefinitions } from 'common/modules/plans/plans/definitions';
import {
	Plan,
	Plans,
	SubscriptionPlan,
	MerchantSolutionDefinitions,
	getPreviousPlanName,
} from 'common/modules/plans';
import { PlanKeyFeature } from './types';
import {
	getDefaultCommissionPercentageInDecimals,
	getDefaultFixedCommissionInCents,
} from 'common/constants/paymentCommissions';
import { getPricingString } from 'common/utils/common';
import { TFunction } from 'i18next';
import { BILLING_CURRENCY } from 'common/modules/billing';
import { CurrencyObject } from 'common/types';

export const getPlanPriceString = (plan: Plan, currency?: CurrencyObject, discount?: number) => {
	const pricePerMonth = PlanDefinitions[plan].pricing?.fixed?.amount.value ?? 0;
	const discountMultiplier = discount ? 1 - discount : 1;
	const price = pricePerMonth * discountMultiplier;
	return getPricingString(price, currency);
};

export const getPlanPaymentsPrice = (plan: SubscriptionPlan) => {
	const percentage = getDefaultCommissionPercentageInDecimals(plan, 'CARD_ONLINE');
	const fixed = getDefaultFixedCommissionInCents(plan, 'USD', 'CARD_ONLINE');

	return `${(100 * percentage).toFixed(1)}% + ${getPricingString(
		fixed.fixedInCents,
		BILLING_CURRENCY,
	)}`;
};

export const getPlanDeliveryPrice = (plan: SubscriptionPlan, t: TFunction) => {
	const pricing = MerchantSolutionDefinitions['DELIVERY'].pricing;
	const price =
		pricing.byPlan?.[plan]?.fixed?.amount.value ?? pricing.default?.fixed?.amount.value ?? 0;

	return t('plans.deliveryPrice2', {
		price: getPricingString(price, BILLING_CURRENCY),
		defaultValue: '{{price}} /order',
	});
};

export const getPlanApiPrice = (plan: SubscriptionPlan, t: TFunction) => {
	const pricing = MerchantSolutionDefinitions['API_WEBHOOKS'].pricing;
	const price =
		pricing.byPlan?.[plan]?.fixed?.amount.value ?? pricing.default?.fixed?.amount.value ?? 0;

	return t('plans.apiPrice', {
		price: getPricingString(price, BILLING_CURRENCY),
		defaultValue: '{{price}} / 100 calls',
	});
};

export const getPlanDescription = (plan: Plan, t: TFunction) => {
	switch (plan) {
		case Plans.LITE:
			return t('plans.planDescription.lite', 'Free forever');
		case Plans.BASIC:
			return t('plans.planDescription.basic', 'Best for new businesses starting to sell online');
		case Plans.GROW:
			return t(
				'plans.planDescription.grow',
				'Best for growing businesses selling online and in-store',
			);
		case Plans.ADVANCED:
			return t(
				'plans.planDescription.advanced',
				'Best for scaling businesses that require many features',
			);
		default:
			return '';
	}
};

export const getPlanKeyFeatures = (plan: Plan, t: TFunction): PlanKeyFeature[] => {
	const planDefinition = PlanDefinitions[plan];

	switch (plan) {
		case 'LITE': {
			return [
				{
					title: t('plans.valueProps.freeForever.title', 'Free forever'),
					description: t(
						'plans.valueProps.freeForever.description',
						'Use Rentle without any monthly subscription fees',
					),
					icon: RiGiftLine,
				},
			];
		}

		case 'BASIC': {
			return [
				{
					title: t('plans.valueProps.everythingInPreviousPlan', {
						previousPlan: getPreviousPlanName('BASIC'),
						defaultValue: 'Everything in {{previousPlan}}',
					}),
					hideInConfirm: true,
				},
				{
					title: t('plans.valueProps.customDomains.title', 'Custom domains'),
					description: t(
						'plans.valueProps.customDomains.description',
						'Host your Rentle store at your own www-address',
					),
					icon: RiWindowLine,
				},
				{
					title: t('plans.valueProps.languages.title', 'Languages'),
					description: t(
						'plans.valueProps.languages.description',
						'Allow your customers to shop in their preferred language',
					),
					icon: RiEarthLine,
				},
				{
					title: t('plans.valueProps.delivery.title', 'Delivery'),
					description: t(
						'plans.valueProps.delivery.description',
						'Set up delivery methods to deliver your products to your customers',
					),
					icon: RiTruckLine,
				},
				{
					title: t('plans.valueProps.apiWebhooks.title', 'API & Webhooks'),
					description: t(
						'plans.valueProps.apiWebhooks.description',
						'Build custom integrations with your Rentle store',
					),
					icon: RiCodeSSlashLine,
				},
				{
					title: t('plans.valueProps.blocks.title', {
						blockSlots: planDefinition.blockSlots,
						defaultValue: '{{blockSlots}} Blocks',
					}),
					description: t(
						'plans.valueProps.blocks.description',
						'Customize Rentle with more features',
					),
					icon: RiApps2Line,
				},
			];
		}

		case 'GROW': {
			return [
				{
					title: t('plans.valueProps.everythingInPreviousPlan', {
						previousPlan: getPreviousPlanName('GROW'),
						defaultValue: 'Everything in {{previousPlan}}',
					}),
					hideInConfirm: true,
				},
				{
					title: t('plans.valueProps.manualBookings.title2', 'Manual order creation'),
					description: t(
						'plans.valueProps.manualBookings.description',
						'Sell to your customers in person, over the phone or by email',
					),
					icon: RiAddBoxLine,
				},

				{
					title: t('plans.valueProps.lowerDeliveryFees.title', 'Lower Delivery fees'),
					description: t('plans.valueProps.lowerDeliveryFees.description', {
						price: getPlanDeliveryPrice('GROW', t),
						defaultValue: 'Reduced fees on your deliveries ({{price}})',
					}),
					icon: RiTruckLine,
					hideInPreview: true,
				},
				{
					title: t('plans.valueProps.lowerPaymentCommissions.title', 'Lower payment commissions'),
					description: t('plans.valueProps.lowerPaymentCommissions.description', {
						price: getPlanPaymentsPrice('GROW'),
						defaultValue: 'Lower commissions on your payments ({{price}})',
					}),
					icon: RiBankCardLine,
					hideInPreview: true,
				},
				{
					title: t('plans.valueProps.cheaperApi.title', 'Cheaper API & Webhooks usage'),
					description: t('plans.valueProps.cheaperApi.description', {
						price: getPlanApiPrice('GROW', t),
						defaultValue: 'Lower costs on your API & Webhooks usage ({{price}})',
					}),
					icon: RiCodeSSlashLine,
					hideInPreview: true,
				},
				{
					title: t('plans.valueProps.lowerFeesAndCommissions.title', 'Lower fees and commissions'),
					hideInConfirm: true,
				},
				{
					title: t('plans.valueProps.blocks.title', {
						blockSlots: planDefinition.blockSlots,
						defaultValue: '{{blockSlots}} Blocks',
					}),
					description: t(
						'plans.valueProps.blocks.description',
						'Customize Rentle with more features',
					),
					icon: RiApps2Line,
				},
			];
		}

		case 'ADVANCED': {
			return [
				{
					title: t('plans.valueProps.everythingInPreviousPlan', {
						previousPlan: getPreviousPlanName('ADVANCED'),
						defaultValue: 'Everything in {{previousPlan}}',
					}),
					hideInConfirm: true,
				},
				{
					title: t('plans.valueProps.checkIn.title', 'Check-in App'),
					description: t(
						'plans.valueProps.checkIn.description',
						'The in-store solution for your retail operations.',
					),
					icon: RiTabletLine,
				},
				{
					title: t('plans.valueProps.lowestDeliveryFees.title', 'Lowest Delivery fees'),
					description: t('plans.valueProps.lowestDeliveryFees.description', {
						price: getPlanDeliveryPrice('ADVANCED', t),
						defaultValue: 'The lowest fees on your deliveries ({{price}})',
					}),
					icon: RiTruckLine,
					hideInPreview: true,
				},

				{
					title: t('plans.valueProps.lowestPaymentCommissions.title', 'Lowest payment commissions'),
					description: t('plans.valueProps.lowestPaymentCommissions.description', {
						price: getPlanPaymentsPrice('ADVANCED'),
						defaultValue: 'The lowest commissions on payments ({{price}})',
					}),
					icon: RiBankCardLine,
					hideInPreview: true,
				},
				{
					title: t('plans.valueProps.cheapestApiUsage.title', 'Cheapest API & Webhooks usage'),
					description: t('plans.valueProps.cheapestApiUsage.description', {
						price: getPlanApiPrice('ADVANCED', t),
						defaultValue: 'The lowest costs on your API & Webhooks usage ({{price}})',
					}),
					icon: RiCodeSSlashLine,
					hideInPreview: true,
				},
				{
					title: t(
						'plans.valueProps.lowestFeesAndCommissions.title',
						'Lowest fees and commissions',
					),
					hideInConfirm: true,
				},
				{
					title: t('plans.valueProps.blocks.title', {
						blockSlots: planDefinition.blockSlots,
						defaultValue: '{{blockSlots}} Blocks',
					}),
					description: t(
						'plans.valueProps.blocks.description',
						'Customize Rentle with more features',
					),
					icon: RiApps2Line,
				},
			];
		}
		default:
			return [];
	}
};
